/** @format */

import {useCallback, useRef, useState} from 'react';

interface Options {
  rootMargin?: string;
  once?: boolean;
}

interface Result<T extends Element> {
  ref: (node: T | null) => void;
  inView: boolean;
}

export const useInView = <T extends Element>({
  rootMargin = '200px',
  once = true,
}: Options = {}): Result<T> => {
  const [inView, setInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();

      if (!node) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (entry?.isIntersecting) {
            setInView(true);
            if (once) {
              const obs = observerRef.current;
              if (obs) {
                obs.disconnect();
              }
            }
          } else if (!once) {
            setInView(false);
          }
        },
        {rootMargin},
      );

      observerRef.current.observe(node);
    },
    [rootMargin, once],
  );

  return {ref, inView};
};
